/**
 * Core domain types (§5.1).
 *
 * Everything in `src/core` is pure: no fetch, no DOM, no Workers globals. These
 * types are the contract between the adapters (which do I/O) and the engine
 * (which does not).
 */

export type Position = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';

export const FANTASY_POSITIONS: readonly Position[] = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

/** A Sleeper player id. Team defenses use the team abbreviation ("HOU"). */
export type PlayerId = string;

export interface Player {
  player_id_sleeper: PlayerId;
  full_name: string;
  normalized_name: string;
  position: Position | null;
  /** All positions Sleeper considers this player eligible for. */
  fantasy_positions: Position[];
  team: string | null;
  bye_week: number | null;
  age: number | null;
  years_exp: number | null;
  injury_status: string | null;
  /** Sleeper's current roster activity flag, when supplied. */
  active?: boolean | null;
  injury_body_part?: string | null;
  injury_notes?: string | null;
  injury_start_date?: string | null;
  practice_participation?: string | null;
  practice_description?: string | null;
  /** Epoch milliseconds for the latest Sleeper injury/news update. */
  news_updated?: number | null;
  status: string | null;
  /** Sleeper's own popularity ordering. 9999999 is its "unranked" sentinel. */
  search_rank: number | null;
}

export type PlayerAvailability = 'healthy' | 'injured' | 'season_out';

export interface PlayerAvailabilityDetail {
  status: PlayerAvailability;
  reason: string | null;
  source: 'sleeper' | 'override';
  source_name: string | null;
  source_url: string | null;
  reported_at: string | null;
}

export interface SeasonOutOverride {
  designation: 'season_out' | 'available';
  reason: string | null;
  source_name: string;
  source_url: string;
  reported_at: string;
}

/** The player universe, keyed by Sleeper id. */
export type PlayerUniverse = Map<PlayerId, Player>;

/**
 * One row of an external ranking source, already resolved to a Sleeper player.
 * `player_key` is null only for rows the matcher could not resolve; those are
 * reported, never silently dropped (FR-19).
 */
export interface RankingRow {
  source_id: string;
  player_key: PlayerId | null;
  /** Name exactly as it appeared in the source, for unmatched reporting. */
  raw_name: string;
  overall_rank: number;
  position_rank: number | null;
  tier: number | null;
  team: string | null;
  position: Position | null;
  notes: string | null;
}

export type ScoringFormat = 'standard' | 'half-ppr' | 'ppr' | 'unknown';

export interface RankingSource {
  source_id: string;
  /** Human label shown in the UI, e.g. "Hayden Winks (Yahoo)". */
  source_name: string;
  /** R3: rendered as a clickable attribution link. Never stripped. */
  source_url: string | null;
  /** ISO date the underlying list was published or fetched. */
  as_of_date: string;
  scoring_format: ScoringFormat;
  /** Which permission tier this source falls under (§4). */
  tier: 1 | 2 | 3;
  /**
   * "rank" sources express expert/consensus opinion; "market" sources express
   * trade value; "adp" expresses where players actually go. The UI labels these
   * differently on purpose (§4.3.3).
   */
  kind: 'rank' | 'market' | 'adp' | 'user';
  rows: RankingRow[];
  /** Rows that could not be resolved to a Sleeper id (FR-19). */
  unmatched: UnmatchedRow[];
}

export interface UnmatchedRow {
  raw_name: string;
  overall_rank: number;
  team: string | null;
  position: Position | null;
  /** Best fuzzy candidates from the player universe, for the "this is X" UI. */
  suggestions: { player_key: PlayerId; full_name: string; score: number }[];
}

/** A projected stat line. Keys mirror Sleeper's scoring_settings keys. */
export type StatLine = Record<string, number>;

export interface Projection {
  source_id: string;
  player_key: PlayerId;
  stat_line: StatLine | null;
  projected_points_league_scoring: number;
  /** True when derived from a fitted curve rather than a real projection (§4.5). */
  estimated: boolean;
}

export interface LeagueConfig {
  league_id: string;
  name: string;
  season: string;
  teams: number;
  roster_positions: string[];
  scoring_settings: Record<string, number>;
  draft_type: 'snake' | 'linear' | 'auction' | string;
  rounds: number;
  /** 1-based slot the user drafts from, or null if unknown. */
  draft_slot: number | null;
  ppr_value: number;
  te_premium: number;
  superflex: boolean;
  /** Sleeper's league type: 0 redraft, 1 keeper, 2 dynasty. */
  league_type: 'redraft' | 'keeper' | 'dynasty';
  /**
   * Whether this looks like a rookie-only draft.
   *
   * Sleeper does not flag one, so this is inferred: a keeper or dynasty league
   * running a draft far shorter than its roster. A startup dynasty draft fills
   * every slot; a rookie draft is three to five rounds on top of rosters that
   * already exist.
   */
  rookie_draft: boolean;
  bench_size: number;
  /** Starting slots by position, flex slots counted separately. */
  starters_by_position: Record<string, number>;
  flex_slots: { kind: string; eligible: Position[]; count: number }[];
}

export interface DraftPick {
  pick_no: number;
  round: number;
  draft_slot: number;
  roster_id: number | null;
  player_id: PlayerId;
  picked_by: string | null;
  /** Display name of the drafting team, resolved from league users. */
  team_name: string | null;
  /** True when the user entered this by hand (FR-12). */
  manual?: boolean;
}

export interface DraftState {
  draft_id: string;
  status: string;
  picks: DraftPick[];
  current_pick_no: number;
  on_the_clock_slot: number | null;
  my_draft_slot: number | null;
  my_next_pick_no: number | null;
  my_pick_after_that: number | null;
  my_roster: PlayerId[];
  all_rosters: Record<number, PlayerId[]>;
  /** Epoch ms of the last successful poll (FR-11 staleness indicator). */
  last_synced_at: number | null;
}

export interface ScoreComponents {
  vor_z: number;
  rank_score: number;
  scarcity: number;
  need: number;
  availability: number;
  bye_penalty: number;
  /** Value of the follow-up pick this one leaves you (see `lookahead.ts`). */
  lookahead: number;
}

export interface Valuation {
  player_key: PlayerId;
  proj_points: number;
  replacement_baseline: number;
  vor: number;
  scarcity_score: number;
  need_score: number;
  availability_prob: number;
  /**
   * False when no ADP row exists for this player, in which case
   * `availability_prob` is 0 because nothing is known — not because he is
   * certain to be gone. The UI must never render an unknown as a certainty.
   */
  availability_known: boolean;
  bye_penalty: number;
  final_score: number;
  components: ScoreComponents;
  /** Weighted contribution of each component, for the FR-27 breakdown. */
  contributions: ScoreComponents;
  estimated_projection: boolean;
}

export type BaselineMethod = 'VOLS' | 'VORP' | 'BEER';

export interface EngineWeights {
  vor: number;
  rank: number;
  scarcity: number;
  need: number;
  availability: number;
  bye: number;
  lookahead: number;
}

/**
 * Shipped weights, as shares of the decision.
 *
 * These are readable against each other because every feature is standardised
 * before its weight applies (see `standardize` in `recommend.ts`). They were not
 * readable before that change: the same six numbers used to produce roughly
 * 74% value, 15% ranking, 5% scarcity and under 2% need, because they multiplied
 * features measured in different units.
 *
 * Value leads by a wide margin, and ranking sits well below it on purpose. The
 * two are not independent inputs in this tool — projections are *derived* from
 * the active ranking through the fitted positional curves — so weighting both
 * heavily counts one opinion twice. Ranking is kept as a real but secondary pull
 * so that a source disagreeing with the curve shape still moves the board.
 *
 * `lookahead` is the only term that asks about a pick you have not made yet:
 * how good the follow-up is that this choice leaves you. It is what separates
 * the first quarterback from the fifth when your only quarterback slot is open,
 * which no amount of tuning the other six could do.
 *
 * Still a prior, not a result: §14 calls these "guesses until tested against
 * real drafts", and that remains true. What has changed is that they are now
 * guesses about the right thing.
 */
export const DEFAULT_WEIGHTS: EngineWeights = {
  vor: 0.42,
  rank: 0.12,
  scarcity: 0.1,
  need: 0.09,
  availability: 0.09,
  bye: 0.03,
  lookahead: 0.15,
};

export interface EngineConfig {
  weights: EngineWeights;
  baseline_method: BaselineMethod;
  /** Per-source weights for the blended ranking (FR-15). */
  blend_weights: Record<string, number>;
  /** Rows below this fuzzy-match score are reported rather than accepted. */
  match_threshold: number;
  /** BYOR extraction confidence floor (FR-46, default 0.70). */
  extraction_confidence_threshold: number;
  /** Sleeper poll interval in ms. Never below 1000 (NFR-5). */
  poll_interval_ms: number;
  /** Small penalty applied per bye-week collision at a needed position. */
  bye_penalty_weight: number;
}

export const DEFAULT_ENGINE_CONFIG: EngineConfig = {
  weights: { ...DEFAULT_WEIGHTS },
  baseline_method: 'BEER',
  blend_weights: {},
  match_threshold: 0.86,
  extraction_confidence_threshold: 0.7,
  poll_interval_ms: 5000,
  bye_penalty_weight: 1,
};

export interface Recommendation {
  player: Player;
  valuation: Valuation;
  /** One-sentence, human-readable reason (FR-27). */
  headline: string;
  /** Per-component sentences the UI renders as the breakdown. */
  reasons: string[];
}

/** An ADP observation for one player (§4.4). */
export interface AdpRow {
  player_key: PlayerId | null;
  raw_name: string;
  position: Position | null;
  team: string | null;
  adp: number;
  stdev: number;
  times_drafted: number;
  bye: number | null;
}

export interface AdpData {
  source_name: string;
  source_url: string;
  as_of_date: string;
  /** FR-31: sample-size indicator surfaced in the UI. */
  total_drafts: number;
  scoring_format: ScoringFormat;
  teams: number;
  rows: AdpRow[];
}
